import type { Immutable } from "immer";
import React from "react";
import { OfferModal } from "tsi-common-react/src/components/OfferModal/index";

import type { EverydayOfferGridBlock as EverydayOfferGridBlockValue } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type IProps = Immutable<{ value: EverydayOfferGridBlockValue }>;

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/everyday-offer-grid-block.html``.
 *
 * The outer ``everybody-offer-grid-block`` class is a real typo in the original
 * markup and is reproduced verbatim. Each tile rendered a ``width-65`` Wagtail
 * image, an OfferModal trigger (previously a nested ``include_react`` of
 * ``OfferModalTemplate``, now the component directly as a React child so it
 * still hydrates), a divider bar, and a rich-text description.
 */
export const EverydayOfferGridBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    return (
        <section
            className={concatClassNames([
                "everybody-offer-grid-block",
                styles.everydayOfferGridBlock,
            ])}
        >
            <div className="l-capped-width">
                {(v.tiles ?? []).map((tile, i) => (
                    // Index key: ListBlock tiles don't surface a stable id, so a
                    // CMS reorder reuses positional DOM -- matches the legacy
                    // ``forloop``-positional render.
                    <div
                        className={concatClassNames([
                            "everyday-offer-tile-block",
                            styles.everydayOfferTileBlock,
                        ])}
                        key={i}
                    >
                        {tile.image ? (
                            <img
                                alt={tile.image.default_alt_text}
                                src={tile.image.renditions["width-65"]?.url}
                                loading="lazy"
                                decoding="async"
                            />
                        ) : null}
                        {/* Legacy passed the already-rendered rich-text HTML
                            (``header_text|richtext``) as cta_text; OfferModal
                            dangerouslySetInnerHTMLs it, so pass it raw. */}
                        <OfferModal
                            offerDetailURL={tile.page?.url ?? ""}
                            ctaText={tile.header_text ?? undefined}
                            ctaArrow={false}
                            isButton={false}
                        />
                        <div className="everyday-offer-tile-block__bar"></div>
                        {/* description serializes ALREADY .rich-text-wrapped (RichTextBlock
                            field); legacy `{{ tile.description|richtext }}` rendered
                            `.description > .rich-text`. Inject raw (NOT via <RichTextBlock>,
                            which adds an extra outer <div> -> `.description > div > .rich-text`). */}
                        <div
                            className="everyday-offer-tile-block__description"
                            dangerouslySetInnerHTML={{
                                __html: tile.description ?? "",
                            }}
                        />
                    </div>
                ))}
            </div>
        </section>
    );
};
